/**
 * @fileoverview ユーザー情報取得API
 *
 * このファイルでは、現在認証されているユーザーの情報を
 * バックエンドAPIから取得する機能を提供します。
 */

/**
 * 現在のユーザー情報を取得する
 *
 * 認証済みセッションに基づいて、現在ログインしているユーザーの
 * プロファイル情報をバックエンドAPIから取得します。ユーザーID、
 * 名前、メールアドレスなどの基本情報を含むオブジェクトを返します。
 *
 * @returns {Promise<UserInfo>} ユーザー情報を含むPromise
 * @throws {Error} API通信エラーや認証エラーが発生した場合
 */
import type { UserInfo } from "@/domain/userInfo";
import { fetchWithLog } from "./_fetchWithLog";

export const getUserInfo = async (): Promise<UserInfo> => {
  const bffOrigin = import.meta.env.VITE_BFF_ORIGIN;

  const res = await fetchWithLog(`${bffOrigin}/v1/userinfo`, {
    cache: "no-cache",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error("Failed to request user info.");
  }

  const userInfo = await res.json();
  if (
    !(
      "sub" in userInfo &&
      "name" in userInfo &&
      "email" in userInfo &&
      "email_verified" in userInfo
    ) ||
    !(
      typeof userInfo.sub === "string" &&
      typeof userInfo.name === "string" &&
      typeof userInfo.email === "string" &&
      typeof userInfo.email_verified === "boolean"
    )
  ) {
    throw new Error("Invalid user info.");
  }

  return userInfo as UserInfo;
};
